import { Controller } from 'stimulus'

/**
 * Manages js for Header B(StimulusJS
 * controller)
 *
 * @class HeaderBController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'bulbButton',
    'exit',
    'sidebar',
    'sidebarCompany',
  ]

  /**
   * @function controllers.HeaderBController.initialize
   * @memberof controllers
   * @description Set up eventListeners
   *
   * @return {undefined} manipulates the dom.
   *
   */
  initialize() {
    this.bulbShowCallback = () => { this._showBulb() }
    this.externalRemoveCallback = (event) => { this._handleRemove(event) }

    document.addEventListener('turbolinks:load', this.bulbShowCallback)
    window.addEventListener('sidebar:remove', this.externalRemoveCallback)
  }

  /**
   * @function controllers.HeaderBController.initialMobileToggle
   * @memberof controllers
   * @description Toggles cookie based drawer state
   *
   * @return {undefined} manipulates the dom.
   *
   */
  initialMobileToggle() {
    if (this._isMobile()) {
      this.bulbButtonTargets.forEach(button => {
        button.setAttribute('aria-expanded', false)
        button.classList.toggle('open')
      })
      this.sidebarTarget.classList.toggle('open')
      if (this.hasSidebarCompanyTarget) { this.sidebarCompanyTarget.classList.toggle('open') }
    }
  }

  /**
   * @function controllers.HeaderBController.toggleSidebar
   * @memberof controllers
   * @description Toggles the state of the sidebar for open/close
   *
   * @return {undefined} manipulates the dom.
   *
   */
  toggleSidebar(event) {
    if (event) { event.preventDefault() }

    const sidebarOpen = this._isSidebarOpen()

    this.bulbButtonTargets.forEach(button => {
      button.setAttribute('aria-expanded', !sidebarOpen)
      button.classList.toggle('open')
    })

    if (this._isMobile()) { document.body.classList.toggle('body--no-scroll-mobile') }

    this._toggleDrawerStateCookie()
    this.sidebarTarget.classList.toggle('open')
    if (this.hasSidebarCompanyTarget) { this.sidebarCompanyTarget.classList.toggle('open') }
  }

  // private

  /**
   * @function controllers.HeaderBController._handleRemove
   * @memberof controllers
   * @description Causes all sidebar related elements to disappear, used on the typ
   * @private
   *
   * @return {undefined} manipulates the dom.
   *
   */
  _handleRemove(event) {
    if (this.hasSidebarTarget) {
      this.toggleSidebar(event)

      document.body.classList.remove('body--no-scroll-mobile')
      this._deleteCookie('drawer_state')
      this._deleteCookie('kw')
      this.bulbButtonTargets.forEach(button => {
        button.style.display = 'none'
      })
      this.sidebarTarget.style.display = 'none'
      if (this.hasSidebarCompanyTarget) { this.sidebarCompanyTarget.classList.remove('open') }
    }
  }

  /**
   * @function controllers.HeaderBController._showBulb
   * @memberof controllers
   * @description Shows bulb if sidebar is present
   * @private
   *
   * @return {undefined} manipulates the dom.
   *
   */
  _showBulb() {
    if (this.hasSidebarTarget) {
      this.bulbButtonTargets.forEach(button => {
        button.classList.remove('hidden')
      })
    }
  }


  /**
   * @function controllers.HeaderBController._toggleDrawerStateCookie
   * @memberof controllers
   * @description Toggles the drawer state cookie on toggle of the sidebar
   * @private
   *
   * @return {undefined} manipulates the dom.
   *
   */
  _toggleDrawerStateCookie() {
    const state = this._getCookie('drawer_state')
    const newState = (state === 'open' ? '' : 'open')

    this._deleteCookie('drawer_state')
    this._setCookie('drawer_state', newState)
  }

  /**
   * @function controllers.HeaderBController._getCookie
   * @memberof controllers
   * @description Get specific cookie
   * @private
   *
   * @return {undefined} manipulates the dom.
   *
   */
  _getCookie(name) {
    const v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
    return v ? v[2] : null;
  }

  /**
   * @function controllers.HeaderBController._setCookie
   * @memberof controllers
   * @description Set specific cookie
   * @private
   *
   * @return {undefined} manipulates the dom.
   *
   */
  _setCookie(name, value) {
    document.cookie = name + "=" + value + ';path=/'
  }

  /**
   * @function controllers.HeaderBController._deleteCookie
   * @memberof controllers
   * @description Delete specific cookie
   * @private
   *
   * @return {undefined} manipulates the dom.
   *
   */
  _deleteCookie(name) {
    document.cookie = name + "='';path=/;Max-Age=0"
  }

  /**
   * @function controllers.HeaderBController._isMobile
   * @memberof controllers
   * @description Predicate function for mobile check
   * @private
   *
   * @return {undefined} Checks window width
   *
   */
   _isMobile() {
    return window.innerWidth <= 768
   }

  /**
   * @function controllers.HeaderBController._isSidebarOpen
   * @memberof controllers
   * @description Predicate function for sidebar state
   * @private
   *
   * @return {undefined} Checks aria-expanded
   *
   */
   _isSidebarOpen() {
    return this
   }
}

